import React, { createContext, useContext, ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import { useSocket } from '../hooks/useSocket';

// Define the shape of our context
interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  reconnectAttempts: number;
  connectionError: Error | null;
  reconnect: () => void;
}

// Create the context with default values
const SocketContext = createContext<SocketContextType | undefined>(undefined);

/**
 * Socket.IO context provider that gives the application access to a shared socket connection
 * This provider should be placed high in the component tree to provide socket access broadly.
 * 
 * @example
 * <SocketProvider>
 *   <App />
 * </SocketProvider>
 */
export function SocketProvider({ children }: { children: ReactNode }) {
  // Use our custom hook to manage the socket connection
  const socketData = useSocket();

  return (
    <SocketContext.Provider value={socketData}>
      {children}
    </SocketContext.Provider>
  );
}

/**
 * Custom hook to use the socket context in components
 * 
 * @throws Error if used outside of SocketProvider
 * @example
 * const { socket, isConnected } = useSocketContext();
 */
export function useSocketContext() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocketContext must be used within a SocketProvider');
  }
  return context;
} 